import { FC } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useInstallationStore } from "src/store";
import {
  lengthsOfBoard,
  PURPOSE_OPTIONS,
  purposeOptions,
} from "src/utils/constants";
import { FormSchema } from "src/utils/formSchema";
import { getAllTime } from "src/utils/helpers";
import { PriceTableSchema, useGeneratePriceTable } from "src/utils/priceTable";
import { AnimatedInput, AnimatedTextarea } from "./common/animated-input";
import { AnimatedSelect } from "./AnimatedSelect/AnimatedSelect";
import {
  CitySelector,
  DateSelector,
  FormTitle,
  NumberOfDaysSelector,
} from "./form";
import UploadComponent from "./form/FileUploadControl";
import { FormControl, FormField, FormItem, FormMessage } from "./ui/form";
import PriceTable from "./PriceTable/PriceTable";
import CheckboxWithTooltip from "./CheckboxWithTooltip/CheckboxWithTooltip";

export type RangePickerSelectedDays = {
  from: string;
  to: string;
};

type Props = {
  form: UseFormReturn<FormSchema, unknown>;
};

const DateRange: FC<Props> = ({ form }) => {
  const { installationDetails, setInstallationDetails } =
    useInstallationStore();

  const { t } = useTranslation();
  const priceTableInformation: PriceTableSchema[] = useGeneratePriceTable(form);

  const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInstallationDetails({ withPermission: !e.target.checked });
  };

  const handleTwoSiteBlockChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInstallationDetails({ twoSiteBlock: e.target.checked });
  };

  const timeFrom = form.watch("timeFrom");
  const endOfTimeFrom = Number(timeFrom?.substring(0, 2));
  const startOfTimeTill = endOfTimeFrom < 12 ? 12 : endOfTimeFrom + 1; // timeFrom should be less than timeTill and default to 12

  const isCityEmpty = !installationDetails.city;

  return (
    <div>
      <div className="border">
        <FormTitle />
        <CitySelector form={form} />
        <div className="row">
          <FormField
            control={form.control}
            name="zipCode"
            render={({ field }) => (
              <FormItem className="col-12">
                <FormControl>
                  <AnimatedInput
                    disabled={isCityEmpty}
                    placeholder={t("installations:inputs:zipCode:placeholder")}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="streetAndHouseNumber"
            render={({ field }) => (
              <FormItem className="col-12">
                <FormControl>
                  <AnimatedInput
                    disabled={isCityEmpty}
                    placeholder={t(
                      "installations:inputs:streetAndHouseNumber:placeholder"
                    )}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="row row-no-of-days">
          <div className="col-6">
            <DateSelector form={form} />
          </div>
          <div className="col-6">
            <NumberOfDaysSelector form={form} />
          </div>
        </div>

        <div className="row">
          <FormField
            control={form.control}
            name="timeFrom"
            render={({ field }) => (
              <FormItem className="col-6">
                <FormControl>
                  <AnimatedSelect
                    value={
                      field.value
                        ? { label: field.value, value: field.value }
                        : undefined
                    }
                    options={getAllTime(6, 16).map((time) => ({
                      label: time,
                      value: time,
                    }))}
                    isDisabled={isCityEmpty}
                    placeholder={t(
                      "contactDetails:inputs:timeFrom:placeholder"
                    )}
                    onChange={(e: any) => {
                      field.onChange(e.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timeTill"
            render={({ field }) => (
              <FormItem className="col-6">
                <FormControl>
                  <AnimatedSelect
                    value={
                      field.value
                        ? { label: field.value, value: field.value }
                        : undefined
                    }
                    options={getAllTime(startOfTimeTill, 22).map((time) => ({
                      label: time,
                      value: time,
                    }))}
                    isDisabled={isCityEmpty}
                    placeholder={t(
                      "contactDetails:inputs:timeTill:placeholder"
                    )}
                    onChange={(e: any) => {
                      field.onChange(e.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="row">
          <FormField
            control={form.control}
            name="purpose"
            render={({ field }) => (
              <FormItem className="col-12">
                <FormControl>
                  <AnimatedSelect
                    value={PURPOSE_OPTIONS[field.value]}
                    options={purposeOptions}
                    isDisabled={isCityEmpty}
                    placeholder={t("installations:inputs:purpose:placeholder")}
                    onChange={(e: any) => {
                      field.onChange(e.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lengthOfBoard"
            render={({ field }) => (
              <FormItem className="col-12">
                <FormControl>
                  <AnimatedSelect
                    value={{ label: field.value, value: field.value }}
                    options={lengthsOfBoard.map((len) => ({
                      label: len,
                      value: len,
                    }))}
                    isDisabled={isCityEmpty}
                    placeholder={t(
                      "installations:inputs:lengthOfBoard:placeholder"
                    )}
                    onChange={(e: any) => {
                      field.onChange(e.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="checkbox-container">
          <CheckboxWithTooltip
            id="permission-checkbox"
            tooltipLabel={t(
              "installations:checkboxes:withPermission:tooltipLabel"
            )}
            tooltipContent={t(
              "installations:checkboxes:withPermission:tooltipContent"
            )}
            description={t(
              "installations:checkboxes:withPermission:orderIncludingAuthorisation"
            )}
            checked={!installationDetails.withPermission}
            onChange={handlePermissionChange}
            disabled={isCityEmpty}
          />

          <CheckboxWithTooltip
            id="twoSiteBlock-checkbox"
            tooltipLabel={t(
              "installations:checkboxes:twoSiteBlock:tooltipLabel"
            )}
            tooltipContent={t(
              "installations:checkboxes:twoSiteBlock:tooltipContent"
            )}
            description={t("installations:checkboxes:twoSiteBlock:bothSided")}
            checked={installationDetails.twoSiteBlock}
            onChange={handleTwoSiteBlockChange}
            disabled={isCityEmpty}
          />
        </div>

        <div className="row">
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="col-12">
                <FormControl>
                  <AnimatedTextarea
                    rows={6}
                    disabled={isCityEmpty}
                    placeholder={`${t(
                      `contactDetails:inputs:notes:placeholder`
                    )} (${t(`general:optional`)})`}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <UploadComponent form={form} />

        {!isCityEmpty ? (
          <PriceTable
            priceTableInformation={priceTableInformation}
            form={form}
          />
        ) : null}
      </div>
    </div>
  );
};

export { DateRange };
